// ---------------------------------------------------------------------------
// GR2M model (Génie Rural, monthly, 2 parameters) — Mouelhi, Michel, Perrin,
// Andréassian (2006), "Stepwise development of a two-parameter monthly water
// balance model", J. Hydrology 318. Formulation confirmed by decompiling
// EvalHid's real calculation engine (Evalhid.dll, class Gr2m.calculo_gr2m,
// ilspycmd — same method used for Témez/HBV/GR4J/SAC/AWBM/Nieve1/Nieve2):
// every line below is a direct, line-for-line translation of that
// decompiled method, cross-checked against a hand-derived worked example
// (test/gr2m.test.ts). Also matches INRAE's own official documentation
// (webgr.inrae.fr) and reference source (github.com/amacd31/gr2m) — the two
// agree.
//
// Real (not a porting bug — see above, confirmed straight from the DLL):
// X2 directly multiplies the routing tank (R2 = X2·R1 — `num7 = x2 * num6`
// in the decompiled method), and R2 alone drives the contribution
// (`q = R2²/(R2+60)`), so X2=0 zeroes the flow entirely — this is
// EvalHid's own real behavior, not a deviation from the GR literature (which
// places the "no exchange" value at X2≈1, not 0) introduced by this port.
// X2's default value in baseTemplate.ts is 0mm, so a GR2M zone whose X2
// isn't explicitly edited away from that default silently produces zero
// contribution forever — worth knowing when assigning this model to a zone,
// not something to "fix" here (see calculateGr2m's own test for this exact
// case).
//
// GR2M has no surface component of its own — the whole flow `q` is
// "underground" (Gr2m.cs: esc_sup is always 0, esc_subt = q). Confirmed in
// the real source (2026-08, see CLAUDE.md's engine-source audit): with a
// real aquifer assigned, `contribution` is excluded entirely (`esc =
// esc_sup = 0`) — the sub-basin's own contribution is zero, not just
// missing the base-flow share like the other 5 models; the flow is assumed
// to go to the aquifer's own aggregate balance instead (see aggregation.ts).
//
// `q` is capped at R2 itself, not just at R2²/(R2+60) — `Gr2m.cs:59`:
// `q = Math.Min(r2, Math.Pow(r2, 2) / (r2 + 60))`. In practice R2 is always
// ≥ 0 by the time this runs (floored right above, see pumping below), and
// for any R2 ≥ 0, R2²/(R2+60) ≤ R2 always holds — so the cap never actually
// binds, in the real source either. This port was still missing it
// entirely; ported for line-for-line fidelity with the real source (2026-08,
// see CLAUDE.md's engine-source audit), even though it's a no-op today.
//
// Pumping (`bomb`, mm/step): subtracted from the routing tank (R2) right
// after the X2 exchange, before Q is drawn from it, floored at 0
// (`Gr2m.cs:58`: `r2 = Math.Max(0, r2 - bomb)`). Resolved from the
// sub-basin's own `Ent_Bomb.csv` column and converted to mm/step before
// reaching here — see temez.ts's own header for the full
// resolution/conversion chain, shared by all 6 models. `null` only when
// the sub-basin has no pumping column of its own, or "Modelar bombeos" is
// unchecked.
// ---------------------------------------------------------------------------

export interface Gr2mParameters {
  /** X1 (mm): production tank capacity. */
  x1: number;
  /** X2 (dimensionless in the standard INRAE formulation, "mm" in baseTemplate.ts): water exchange coefficient. */
  x2: number;
}

interface Gr2mState {
  /** So (mm): initial production tank content. */
  so: number;
  /** Ro (mm): initial routing tank content. */
  ro: number;
}

interface Gr2mDetail {
  [key: string]: Float64Array;
  /** S (mm): production tank after the step. */
  s: Float64Array;
  /** Etr (mm): production tank's actual evapotranspiration. */
  etr: Float64Array;
  /** Percolation into the routing tank. */
  percolation: Float64Array;
  /** R (mm): routing tank after the step. */
  r: Float64Array;
  /** Fraction of the routing tank NOT exported by the X2 exchange (informational: doesn't feed any other term). */
  qout: Float64Array;
  /**
   * Q (mm): the flow itself, always calculated — matches `contribution`
   * when `hasAquifer` is false, but (unlike `contribution`) still exposed
   * when `hasAquifer` is true, for `aggregation.ts`'s SUP_SUBT_KEYS (GR2M
   * has no surface component of its own — the whole flow counts as
   * "underground", see this file's own header) — the aquifer's own balance
   * needs this even on the steps where `contribution` itself is zeroed out.
   */
  flow: Float64Array;
}

interface Gr2mResult {
  /** Contribution (flow) per time step, mm. */
  contribution: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: Gr2mDetail;
}

/** Fixed monthly routing tank capacity, the same for every basin (part of GR2M's formulation, not a calibratable parameter). */
const ROUTING_CAPACITY_MM = 60;

/**
 * Simulates the GR2M model for a single sub-basin. `precipitation` and
 * `etp` must already come adjusted (precipAdjustment/etpAdjustment applied) and aligned step
 * by step (monthly, though like the rest of the engines here one step is
 * applied per input series row, without re-aggregating to monthly).
 */
export function calculateGr2m(
  precipitation: Float64Array,
  etp: Float64Array,
  parameters: Gr2mParameters,
  initialState: Gr2mState,
  hasAquifer: boolean,
  pumping: Float64Array | null
): Gr2mResult {
  const { x1, x2 } = parameters;
  const n = precipitation.length;
  const contribution = new Float64Array(n);
  const detail: Gr2mDetail = {
    s: new Float64Array(n),
    etr: new Float64Array(n),
    percolation: new Float64Array(n),
    r: new Float64Array(n),
    qout: new Float64Array(n),
    flow: new Float64Array(n),
  };

  let s = initialState.so;
  let r = initialState.ro;

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    const e = Number.isFinite(etp[t]) ? etp[t] : 0;

    // Rain input into the production tank (tanh-type saturation function).
    const phi = Math.tanh(p / x1);
    const s1 = (s + x1 * phi) / (1 + phi * (s / x1));
    const rainSurplus = p + s - s1;

    // Output via evapotranspiration.
    const psi = Math.tanh(e / x1);
    const s2 = (s1 * (1 - psi)) / (1 + psi * (1 - s1 / x1));
    const etr = s1 - s2;

    // Percolation (cubic).
    const nextS = s2 / (1 + (s2 / x1) ** 3) ** (1 / 3);
    const percolation = s2 - nextS;

    // Routing tank (fixed capacity) with X2 exchange.
    const r1 = r + rainSurplus + percolation;
    const qout = (1 - x2) * r1;
    const bomb = pumping && Number.isFinite(pumping[t]) ? pumping[t] : 0;
    const r2 = Math.max(0, x2 * r1 - bomb);
    const q = Math.min(r2, r2 ** 2 / (r2 + ROUTING_CAPACITY_MM));
    const nextR = r2 - q;

    contribution[t] = hasAquifer ? 0 : q;

    s = nextS;
    r = nextR;

    detail.s[t] = s;
    detail.etr[t] = etr;
    detail.percolation[t] = percolation;
    detail.r[t] = r;
    detail.qout[t] = qout;
    detail.flow[t] = q;
  }

  return { contribution, detail };
}
